import styled from "styled-components";

import { Dispatch, useEffect, useMemo, useRef } from "react";
import { Tab, TabsContainer } from "./Tabs";
import { Mode } from "./InspectionForm";
import Button from "./Buttons/Button";
import useOnlineStatus from "../hooks/useOnlineStatus";

const InspectionFormNavigation = (props: {
  onSave?: () => Promise<boolean | string>;
  saving?: boolean | undefined;
  onComplete?: () => void;
  onReset?: () => void;
  mode?: Mode;
  setMode?: Dispatch<React.SetStateAction<Mode>>;
  inspectionCompleted?: boolean;
  inspectionCompletionDate?: string;
  setLastSavedTime?: Dispatch<React.SetStateAction<string | null>>;
  isRetry?: Boolean;
  fieldYear?: number;
}) => {
  const tabsContainerRef = useRef<HTMLDivElement>();
  const isOnline = useOnlineStatus();
  const canCompleteInspection = useMemo(() => {
    if (!props.fieldYear) {
      // Props are empty, so this is just the fallback rendering of this component.
      return false;
    }

    // Do not allow submitting the form when current date is before fieldYear.
    const fieldYear = new Date(props.fieldYear, 0, 1);
    const result = new Date().getTime() >= fieldYear.getTime();
    return result;
  }, [props.fieldYear]);

  useEffect(() => {
    const scrollActiveTabIntoView = () => {
      const activeTab = tabsContainerRef.current?.querySelector(
        '[data-active="true"]'
      );
      if (activeTab) {
        setTimeout(() => {
          activeTab.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }, 100);
      }
    };
    scrollActiveTabIntoView();
  }, [props]);

  return (
    <NavContainer>
      <NavContent>
        <TabsContainer ref={tabsContainerRef}>
          {!props.inspectionCompleted && (
            <Tab
              key={Mode.FORM}
              data-active={props.mode === Mode.FORM}
              active={props.mode === Mode.FORM}
              onClick={() => props?.setMode && props.setMode(Mode.FORM)}
            >
              Schouwformulier
            </Tab>
          )}
          <Tab
            key={Mode.INFO}
            data-active={props.mode === Mode.INFO}
            active={props.mode === Mode.INFO}
            onClick={() => props?.setMode && props.setMode(Mode.INFO)}
          >
            Basisgegevens
          </Tab>
          <Tab
            key={Mode.CONDITIONS}
            data-active={props.mode === Mode.CONDITIONS}
            active={props.mode === Mode.CONDITIONS}
            onClick={() => props?.setMode && props.setMode(Mode.CONDITIONS)}
          >
            Pakketvoorwaarden
          </Tab>
          <Tab
            key={Mode.DETAILS}
            data-active={props.mode === Mode.DETAILS}
            active={props.mode === Mode.DETAILS}
            onClick={() => props.setMode && props.setMode(Mode.DETAILS)}
          >
            Details beheereenheid
          </Tab>
          <Tab
            key={Mode.REVIEW}
            data-active={props.mode === Mode.REVIEW}
            active={props.mode === Mode.REVIEW}
            onClick={() => props.setMode && props.setMode(Mode.REVIEW)}
          >
            Overzicht bevindingen
          </Tab>
        </TabsContainer>
        <ButtonsContainer>
          {props.mode === Mode.REVIEW ? (
            <Button
              label={
                props.inspectionCompletionDate
                  ? `Ingediend bij collectief op ${
                      props.inspectionCompletionDate &&
                      new Date(
                        props?.inspectionCompletionDate
                      )?.toLocaleDateString()
                    }`
                  : "Indienen bij collectief"
              }
              color="green"
              disabled={
                !props.onComplete ||
                props.inspectionCompleted ||
                !isOnline ||
                !canCompleteInspection
              }
              onClick={() => props.onComplete && props.onComplete()}
            />
          ) : null}

          {props.onReset && (
            <Button
              label="Formulier leeg maken"
              color="blue"
              onClick={() => props.onReset && props.onReset()}
            />
          )}
          {props.mode === Mode.FORM ? (
            <Button
              label="Opslaan"
              color="green"
              disabled={props?.saving}
              onClick={() => props.onSave && props.onSave()}
            />
          ) : null}
        </ButtonsContainer>
      </NavContent>
    </NavContainer>
  );
};

const NavContainer = styled.div`
  justify-self: flex-start;
  height: 58px;
  background: white;
  padding-bottom: 0;
  display: flex;

  align-content: center;
  justify-content: center;
`;
const NavContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 70vw;
  @media only screen and (max-width: 1250px) {
    width: 100vw;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;
export default InspectionFormNavigation;
